// User-entered data may be cached in localStorage when offline; treat the device as trusted or clear on shared machines (logout clears sparro_* keys).
import { db } from './firebase';

const STORAGE_KEY = 'sparro_user_data';

export const saveUserData = (userData) => {
  try {
    const existingData = getStoredData();
    const newEntry = {
      id: Date.now() + Math.random(), // Simple unique ID
      timestamp: new Date().toISOString(),
      ...userData
    };
    
    existingData.push(newEntry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
    
    console.log('User data saved:', newEntry);
    return { success: true, id: newEntry.id };
  } catch (error) {
    console.error('Error saving user data:', error);
    return { success: false, error: error.message };
  }
};

export const getStoredData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error retrieving data:', error);
    return [];
  }
};

// Saved portal strategies: Firestore per user when uid is set, else localStorage
const STRATEGIES_KEY = 'sparro_portal_strategies';

const STRATEGIES_CACHE_MS = 5000;
let strategiesCache = { uid: null, list: null, at: 0 };

function getSavedStrategiesFromStorage() {
  try {
    const data = localStorage.getItem(STRATEGIES_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error retrieving strategies:', error);
    return [];
  }
}

/**
 * Get saved strategies. With uid (logged-in user): loads from Firestore. Without uid: from localStorage.
 * On first load with uid, migrates any strategies from localStorage to Firestore and then loads from Firestore.
 * @param {string|null|undefined} uid - Firebase user id. If provided and db exists, uses Firestore.
 * @returns {Promise<Array>} List of strategy objects with id, name, timestamp, ...
 */
export async function getSavedStrategies(uid) {
  if (uid && db && db.collection) {
    const now = Date.now();
    if (strategiesCache.uid === uid && strategiesCache.list && now - strategiesCache.at < STRATEGIES_CACHE_MS) {
      return strategiesCache.list;
    }
    try {
      const local = getSavedStrategiesFromStorage();
      if (local.length > 0) {
        for (const s of local) {
          await db.collection('users').doc(uid).collection('strategies').add({
            timestamp: s.timestamp || new Date().toISOString(),
            name: s.name || 'Strategie',
            totalDebt: s.totalDebt,
            repaymentPlan: s.repaymentPlan,
            hasStartedRepaying: s.hasStartedRepaying ?? false,
            yearsAlreadyRepaid: s.yearsAlreadyRepaid ?? 0,
            oneTimeRepayments: Array.isArray(s.oneTimeRepayments) ? s.oneTimeRepayments : [],
            minPayment: s.minPayment,
            monthlyPayment: s.monthlyPayment,
            enableGrowth: s.enableGrowth,
            growthRate: s.growthRate,
            enableSeparateGrowth: s.enableSeparateGrowth ?? false,
            investmentGrowthRate: s.investmentGrowthRate ?? 0,
            enableInvestment: s.enableInvestment,
            monthlyInvestment: s.monthlyInvestment,
            enableDelay: s.enableDelay,
            delayYears: s.delayYears,
            delayMonths: s.delayMonths,
            startingInvestmentCapital: s.startingInvestmentCapital ?? 0,
            enableCustomRates: !!s.enableCustomRates,
            customRates: Array.isArray(s.customRates) ? s.customRates : [],
          });
        }
        localStorage.removeItem(STRATEGIES_KEY);
      }
      const snap = await db.collection('users').doc(uid).collection('strategies').orderBy('timestamp', 'desc').limit(100).get();
      const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      strategiesCache = { uid, list, at: Date.now() };
      return list;
    } catch (error) {
      console.error('Error loading strategies from Firestore:', error);
      return [];
    }
  }
  return Promise.resolve(getSavedStrategiesFromStorage());
}

export function invalidateStrategiesCache() {
  strategiesCache = { uid: null, list: null, at: 0 };
}

/**
 * Save a strategy. With uid: save to Firestore. Without: save to localStorage.
 * @param {object} strategy - Strategy fields (name, totalDebt, repaymentPlan, ...)
 * @param {string|null|undefined} uid - Firebase user id
 * @returns {Promise<{success: boolean, id?: string, error?: string}>}
 */
export async function savePortalStrategy(strategy, uid) {
  const entry = {
    timestamp: new Date().toISOString(),
    name: strategy.name || 'Strategie',
    totalDebt: strategy.totalDebt,
    repaymentPlan: strategy.repaymentPlan,
    hasStartedRepaying: strategy.hasStartedRepaying ?? false,
    yearsAlreadyRepaid: strategy.yearsAlreadyRepaid ?? 0,
    oneTimeRepayments: Array.isArray(strategy.oneTimeRepayments) ? strategy.oneTimeRepayments : [],
    minPayment: strategy.minPayment,
    monthlyPayment: strategy.monthlyPayment,
    enableGrowth: strategy.enableGrowth,
    growthRate: strategy.growthRate,
    enableSeparateGrowth: strategy.enableSeparateGrowth ?? false,
    investmentGrowthRate: strategy.investmentGrowthRate ?? 0,
    enableInvestment: strategy.enableInvestment,
    monthlyInvestment: strategy.monthlyInvestment,
    enableDelay: strategy.enableDelay,
    delayYears: strategy.delayYears,
    delayMonths: strategy.delayMonths,
    startingInvestmentCapital: strategy.startingInvestmentCapital ?? 0,
    enableCustomRates: !!strategy.enableCustomRates,
    customRates: Array.isArray(strategy.customRates) ? strategy.customRates : [],
  };

  if (uid && db && db.collection) {
    try {
      const ref = await db.collection('users').doc(uid).collection('strategies').add(entry);
      invalidateStrategiesCache();
      return { success: true, id: ref.id };
    } catch (error) {
      console.error('Error saving strategy to Firestore:', error);
      return { success: false, error: error.message };
    }
  }

  try {
    const list = getSavedStrategiesFromStorage();
    const id = String(Date.now() + Math.random());
    list.push({ id, ...entry });
    localStorage.setItem(STRATEGIES_KEY, JSON.stringify(list));
    return { success: true, id };
  } catch (error) {
    console.error('Error saving strategy:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a strategy by id. With uid: delete from Firestore. Without: from localStorage.
 * @param {string} id - Strategy document id
 * @param {string|null|undefined} uid - Firebase user id
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deletePortalStrategy(id, uid) {
  if (uid && db && db.collection) {
    try {
      await db.collection('users').doc(uid).collection('strategies').doc(id).delete();
      invalidateStrategiesCache();
      return { success: true };
    } catch (error) {
      console.error('Error deleting strategy from Firestore:', error);
      return { success: false, error: error.message };
    }
  }
  try {
    const list = getSavedStrategiesFromStorage().filter((s) => s.id !== id);
    localStorage.setItem(STRATEGIES_KEY, JSON.stringify(list));
    return { success: true };
  } catch (error) {
    console.error('Error deleting strategy:', error);
    return { success: false };
  }
}
